// Day planning (SPEC §2), pure and deterministic: each day's key, kit, instruments, FX and seed
// from the day before. Used by the server's scheduler and by the static demo.
//  - the key moves by related steps (relative major/minor, a fifth up or down, sometimes a whole
//    step), never staying put;
//  - kit and instruments rotate so consecutive days differ;
//  - kit + preset pairings make "genre days";
//  - every day has a Classics family (piano, strings, guitar, brass, flute…) on its own slot.
import type { Bpm } from "./clock";
import { DELAY_TICKS, makePeriodDef, type PeriodDef } from "./period";
import { KIT_IDS, KITS, type KitId } from "./kits";
import { type Key } from "./music";
import { defaultAtmosKit } from "./atmos";
import { defaultClassic } from "./classics";
import { presetsForRole, type InstrumentRole } from "./presets";
import { hashString, mixSeed, Rng } from "./rng";

const SEED_BASE = hashString("endless-beat/periods/v1");

/** The very first day's character (if nothing came before it). */
export const FIRST_PERIOD = {
  key: { tonic: 9, mode: "minor" } as Key,
  kit: "jungle" as KitId,
  instruments: { bass: "ladder-bass", harmony: "chorus-pad", lead: "deep-organ", classic: "grand-piano" },
};

/** The slow stream's first day: trap, D minor, an 808-style sub and a Rhodes-like piano. */
export const FIRST_SLOW_PERIOD = {
  key: { tonic: 2, mode: "minor" } as Key,
  kit: "trap_lab" as KitId,
  instruments: { bass: "grit-sub", harmony: "fm-epiano", lead: "deep-organ", classic: "grand-piano" },
};

/** Kit weights of the slow stream (72 BPM): trap, dub and slow jams first; every kit stays possible
 *  (a jungle break at 72 BPM is a double-time 144 feel). */
export const SLOW_KIT_WEIGHTS: Record<KitId, number> = {
  trap_lab: 4,
  deep_sea: 3,
  garage: 2.5,
  afro: 2,
  synthwave: 2,
  jungle: 1.5,
  deep_house: 1.2,
  disco: 1,
  techno: 0.5,
};

/** Instruments the slow stream likes more (sub basses, keys and pads for slow jams). */
const SLOW_FAVOURS: Record<string, number> = {
  "grit-sub": 1.6,
  "boom-bass": 1.5,
  "wood-bass": 1.3,
  "fm-epiano": 1.6,
  "chorus-pad": 1.4,
  "jazz-organ": 1.3,
  "polysynth-pad": 1.3,
  "disco-epiano": 1.3,
  "deep-organ": 1.3,
};

/** Preset preferences per kit: the "genre day" pairings (weights). */
export const PAIRINGS: Record<KitId, Record<Exclude<InstrumentRole, "classic" | "atmos">, Record<string, number>>> = {
  jungle: {
    bass: { "ladder-bass": 3, "wood-bass": 2, "boom-bass": 2, "acid-line": 1, "grit-sub": 1, "reese-bass": 2 },
    harmony: { "chorus-pad": 3, "fm-epiano": 2, "poly-stab": 2, "jazz-organ": 1, "full-drawbars": 1, "rave-piano": 2 },
    lead: {
      "deep-organ": 3,
      "ladder-lead": 2,
      "square-squelch": 1,
      "ladder-brass": 1,
      "vox-lead": 1,
      "seventies-lead": 1,
    },
  },
  deep_sea: {
    bass: { "boom-bass": 3, "wood-bass": 2, "ladder-bass": 2, "grit-sub": 1, "acid-line": 1, "fat-mono-bass": 1 },
    harmony: {
      "fm-epiano": 3,
      "chorus-pad": 3,
      "jazz-organ": 1,
      "poly-stab": 1,
      "full-drawbars": 1,
      "deep-chord": 2,
      "string-ensemble": 1,
    },
    lead: {
      "deep-organ": 3,
      "ladder-lead": 2,
      "square-squelch": 1,
      "ladder-brass": 1,
      "vox-lead": 2,
      "minimal-blip": 1,
    },
  },
  trap_lab: {
    bass: { "grit-sub": 3, "boom-bass": 3, "acid-line": 1, "ladder-bass": 1, "wood-bass": 1, "reese-bass": 1 },
    harmony: {
      "poly-stab": 3,
      "chorus-pad": 2,
      "fm-epiano": 2,
      "full-drawbars": 1,
      "jazz-organ": 1,
      "string-ensemble": 1,
    },
    lead: { "ladder-brass": 2, "square-squelch": 2, "deep-organ": 2, "ladder-lead": 2, "vox-lead": 2, "sky-lead": 1 },
  },
  deep_house: {
    bass: {
      "acid-line": 3,
      "ladder-bass": 2,
      "wood-bass": 2,
      "boom-bass": 1,
      "grit-sub": 1,
      "rubber-bass": 3,
      "fat-mono-bass": 1,
    },
    harmony: {
      "jazz-organ": 3,
      "full-drawbars": 2,
      "fm-epiano": 2,
      "poly-stab": 2,
      "chorus-pad": 1,
      "deep-chord": 4,
      "rave-piano": 2,
      "string-ensemble": 1,
    },
    lead: {
      "ladder-lead": 3,
      "square-squelch": 2,
      "deep-organ": 2,
      "ladder-brass": 1,
      "vox-lead": 2,
      "seventies-lead": 2,
      "minimal-blip": 2,
    },
  },
  techno: {
    bass: { "rumble-sub": 4, "rolling-bass": 4, "acid-line": 2, "hoover-bass": 2, "reese-bass": 2, "rubber-bass": 3 },
    harmony: { "dub-chord": 5, "poly-stab": 1, "chorus-pad": 1, "trance-stack": 2, "deep-chord": 2 },
    lead: { "hoover-lead": 4, "square-squelch": 2, "ladder-lead": 1, "euphoria-lead": 2, "minimal-blip": 3 },
  },
  disco: {
    bass: { "octave-bass": 5, "wood-bass": 1, "ladder-bass": 1, "fat-mono-bass": 3 },
    harmony: {
      "string-section": 4,
      "disco-epiano": 4,
      "full-drawbars": 1,
      "anthem-saws": 1,
      "string-ensemble": 3,
      "rave-piano": 1,
    },
    lead: { "disco-lead": 4, "ladder-brass": 2, "deep-organ": 1, "seventies-lead": 3 },
  },
  garage: {
    bass: { "organ-bass": 4, "two-step-sub": 4, "boom-bass": 1, "rubber-bass": 1 },
    harmony: { "garage-chord": 5, "fm-epiano": 1, "poly-stab": 1, "rave-piano": 2, "deep-chord": 1 },
    lead: { "vocal-pluck": 5, "deep-organ": 1, "siren-vox": 2 },
  },
  afro: {
    bass: { "log-drum": 5, "boom-bass": 1, "wood-bass": 1 },
    harmony: { "highlife-keys": 5, "fm-epiano": 1, "chorus-pad": 1, "deep-chord": 1 },
    lead: { "flute-lead": 4, "deep-organ": 2 },
  },
  synthwave: {
    bass: { "arp-bass": 5, "ladder-bass": 1, "fat-mono-bass": 2 },
    harmony: { "polysynth-pad": 5, "chorus-pad": 1, "anthem-saws": 2, "string-ensemble": 2 },
    lead: { "saw-solo": 5, "ladder-lead": 1, "sky-lead": 2, "seventies-lead": 2 },
  },
};

const REVERB_BY_STYLE: Record<string, [number, number]> = {
  breakbeat: [0.45, 0.7],
  dub: [0.65, 0.9],
  halftime: [0.3, 0.55],
  four_floor: [0.4, 0.65],
};

export function periodSeed(day: number): number {
  return mixSeed(SEED_BASE, day >>> 0) ^ (day < 0 ? 0x5a5a5a5a : 0);
}

/** Key motion from the previous day (never stays on the same key). */
export function nextKey(prev: Key, rng: Rng): Key {
  const t = prev.tonic;
  const rel: Key =
    prev.mode === "minor" ? { tonic: (t + 3) % 12, mode: "major" } : { tonic: (t + 9) % 12, mode: "minor" };
  const options: { item: Key; weight: number }[] = [
    { item: rel, weight: 4 },
    { item: { tonic: (t + 7) % 12, mode: prev.mode }, weight: 2 }, // a fifth up
    { item: { tonic: (t + 5) % 12, mode: prev.mode }, weight: 2 }, // a fifth down
    { item: { tonic: (t + 2) % 12, mode: prev.mode }, weight: 0.6 }, // whole step up
    { item: { tonic: (t + 10) % 12, mode: prev.mode }, weight: 0.6 }, // whole step down
  ];
  return rng.weighted(options);
}

function pickWeighted<T extends string>(rng: Rng, weights: Record<T, number>, exclude: readonly string[]): T {
  const items = (Object.entries(weights) as [T, number][])
    .filter(([k]) => !exclude.includes(k))
    .map(([item, weight]) => ({ item, weight }));
  return rng.weighted(
    items.length ? items : (Object.entries(weights) as [T, number][]).map(([item, weight]) => ({ item, weight })),
  );
}

/** Plan day `day` given the day before (pure and deterministic). */
export function planPeriod(
  day: number,
  prev: PeriodDef | null,
  beforePrev: PeriodDef | null = null,
  bpm: Bpm = 125,
): PeriodDef {
  const slow = bpm === 72;
  // The slow stream has its own days: another seed stream, its own kit preferences.
  const seed = slow ? mixSeed(periodSeed(day), 0x72) : periodSeed(day);
  const rng = new Rng(seed);
  if (!prev) {
    const first = slow ? FIRST_SLOW_PERIOD : FIRST_PERIOD;
    return makePeriodDef({
      bpm,
      day,
      ...first,
      instruments: first.instruments,
      fx: { reverbSize: 0.6, delayTicks: slow ? 480 : 720 },
      seed,
    });
  }
  const key = nextKey(prev.key, rng);
  // Kit: never the same as yesterday; prefer not the day before either.
  const kitWeights = Object.fromEntries(
    KIT_IDS.map((k) => [k, (slow ? SLOW_KIT_WEIGHTS[k] : 1) * (k === beforePrev?.kit ? 0.4 : 1)]),
  ) as Record<KitId, number>;
  const kit = pickWeighted(rng, kitWeights, [prev.kit]);
  const instruments = {} as Record<InstrumentRole, string>;
  for (const role of ["bass", "harmony", "lead"] as const) {
    const weights = { ...PAIRINGS[kit][role] };
    // Everything else stays possible, but rarely: days sound like their kit's genre.
    for (const p of presetsForRole(role)) weights[p.id] ??= 0.03;
    if (slow) for (const id of Object.keys(weights)) weights[id] *= SLOW_FAVOURS[id] ?? 1;
    instruments[role] = pickWeighted(rng, weights, [prev.instruments[role]]);
  }
  // The Classics family: its own stream of the day's seed (the other picks don't change), not
  // yesterday's.
  const chosen = {
    ...instruments,
    classic: defaultClassic(
      kit,
      seed,
      [prev.instruments.classic],
      [instruments.bass, instruments.harmony, instruments.lead],
    ),
  };
  // The atmos kit: its own stream too, not yesterday's (days planned before kits have none).
  const [lo, hi] = REVERB_BY_STYLE[KITS[kit].style];
  const reverbSize = Math.round(rng.range(lo, hi) * 100) / 100;
  const delayTicks = rng.pick(DELAY_TICKS.filter((d) => d !== prev.fx.delayTicks));
  const atmosKit = defaultAtmosKit(kit, seed, [prev.atmosKit ?? ""]);
  return makePeriodDef({ bpm, day, key, kit, instruments: chosen, atmosKit, fx: { reverbSize, delayTicks }, seed });
}

/** Plan a run of days from a starting point (used by tests and the scheduler). */
export function planRange(
  fromDay: number,
  count: number,
  prev: PeriodDef | null,
  beforePrev: PeriodDef | null = null,
  bpm: Bpm = 125,
): PeriodDef[] {
  const out: PeriodDef[] = [];
  let p = prev;
  let pp = beforePrev;
  for (let d = fromDay; d < fromDay + count; d++) {
    const def = planPeriod(d, p, pp, bpm);
    out.push(def);
    pp = p;
    p = def;
  }
  return out;
}
